<template>
  <v-card>
    <v-card-text>
      <b-input
        v-model="form.query"
        :error-messages="getErrorMessages(v$.query)"
        class="mb-5"
        label="Текст для пошуку"
        placeholder="Введіть слово або набір слів..."
        prepend-inner-icon="mdi-magnify"
        @blur="v$.query.$touch()"></b-input>

      <div class="d-flex align-center ga-2 mb-3">
        <v-icon color="secondary" icon="mdi-circle" size="10"></v-icon>
        <span
          class="text-uppercase font-weight-bold text-medium-emphasis font-size-11">
          Параметри пошуку
        </span>
      </div>
      <v-divider class="mb-3"></v-divider>

      <div
        class="font-size-14 font-weight-bold text-disabled text-uppercase mb-3">
        Шукати в
      </div>
      <div class="d-flex flex-column ga-3 mb-6">
        <v-card
          v-for="scope in scopes"
          :key="scope.key"
          :class="{ 'search-option-card--active': form.scopes[scope.key] }"
          border
          class="search-option-card"
          @click="toggleScope(scope.key)">
          <v-card-text class="d-flex align-start ga-3">
            <b-checkbox
              v-model="form.scopes[scope.key]"
              class="mt-n2"
              color="secondary"
              hide-details
              @click.stop></b-checkbox>
            <div>
              <div class="font-size-13 font-weight-bold">
                {{ scope.title }}
              </div>
              <div class="font-size-11 text-disabled">
                {{ scope.description }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div
        class="font-size-13 font-weight-bold text-disabled text-uppercase mb-3">
        Параметри
      </div>
      <v-row>
        <v-col v-for="option in options" :key="option.key" cols="12" md="6">
          <v-card
            border
            class="search-option-card"
            @click="toggleOption(option.key)">
            <v-card-text class="d-flex align-start ga-3">
              <b-checkbox
                v-model="form.options[option.key]"
                class="mt-n2"
                color="secondary"
                hide-details
                @click.stop></b-checkbox>
              <div>
                <div class="font-size-13 font-weight-bold">
                  {{ option.title }}
                </div>
                <div class="font-size-11 text-disabled">
                  {{ option.description }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-6"></v-divider>

      <div class="d-flex flex-wrap ga-3">
        <v-btn
          :loading="$loading.isLoading('edsSearchFullText')"
          prepend-icon="mdi-magnify"
          @click="handleSearch">
          <span class="font-weight-light">Виконати пошук</span>
        </v-btn>
        <v-btn
          border
          color="white"
          prepend-icon="mdi-restore"
          @click="handleReset">
          <span class="font-weight-light"> Очистити форму</span>
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { BCheckbox, BInput } from 'best-modules/components'
import { useSearch } from '@/pages/search/search'
import { useVuelidate } from '@vuelidate/core'
import { minLength, required } from '@vuelidate/validators'
import { getErrorMessages } from 'best-modules/utils'
import type { SearchOptionKey, SearchScopeKey } from '@/utils/types/search'

defineOptions({
  name: 'FullTextSearch',
})

const scopes: {
  key: SearchScopeKey
  title: string
  description: string
}[] = [
  {
    key: 'title',
    title: 'Назва документа',
    description: 'Шукати у назвах документів',
  },
  {
    key: 'body',
    title: 'Тіло документа',
    description: 'Шукати у вмісті прикріплених файлів',
  },
  {
    key: 'attributes',
    title: 'Значення атрибутів',
    description: 'Шукати у значеннях полів картки документа',
  },
]

const options: {
  key: SearchOptionKey
  title: string
  description: string
}[] = [
  {
    key: 'exactPhrase',
    title: 'Точне співпадіння фрази',
    description: 'Шукати фразу як цільну послідовність слів',
  },
  {
    key: 'caseSensitive',
    title: 'Враховувати регістр',
    description: 'Розрізняти ВЕЛИКІ та малі літери',
  },
]

const { fullTextForm: form, resetFullTextForm, search } = useSearch()
const rules = {
  query: { required, minLength: minLength(3) },
}
const v$ = useVuelidate(rules, form)

const toggleScope = (key: SearchScopeKey) => {
  form.scopes[key] = !form.scopes[key]
}

const toggleOption = (key: SearchOptionKey) => {
  form.options[key] = !form.options[key]
}

const handleSearch = async () => {
  const isValid = await v$.value.$validate()
  if (!isValid) return

  search('fullText', 'Повнотекстовий пошук')
}

const handleReset = () => {
  resetFullTextForm()
  v$.value.$reset()
}
</script>

<style lang="scss" scoped>
.search-option-card {
  transition: background-color 0.15s, border-color 0.15s;

  &--active {
    background-color: rgb(var(--v-theme-light-blue));
    border-color: rgb(var(--v-theme-secondary)) !important;
  }
}
</style>
